import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../supabaseClient";
import { API_BASE_URL } from "../config";
import { toast } from "react-toastify";
import {
    FiSend, FiUser, FiMessageSquare, FiClock, FiXCircle, FiInfo, FiSearch,
    FiChevronLeft, FiMail, FiTag, FiActivity,
} from "react-icons/fi";
import { Button, Card, PageHeader, StatusBadge, EmptyState, Modal, useConfirm } from "./ui";

/* Read-only customer info for the selected user (no writes). */
function UserInfoPanel({ userId, state, lastMessage, info }) {
    const rows = [
        { label: "User ID", value: userId, icon: FiUser },
        { label: "Kode Toko", value: info?.kode_toko || "-", icon: FiTag },
        { label: "Email", value: info?.email || "-", icon: FiMail },
        { label: "Jenis", value: info?.jenis || "-", icon: FiTag },
        { label: "Status", value: state?.chat_status || (state?.step === "chat_mode" ? "active" : "offline"), icon: FiActivity },
    ];

    return (
        <div className="flex h-full flex-col">
            <div className="border-b border-slate-100 px-5 py-4 dark:border-slate-800">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Informasi User</h3>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Detail pelanggan</p>
            </div>
            <div className="flex-1 overflow-y-auto p-5 scrollbar-slim">
                <div className="mb-5 flex flex-col items-center text-center">
                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-50 text-primary-600 dark:bg-primary-500/10 dark:text-primary-400">
                        <FiUser className="h-6 w-6" />
                    </span>
                    <p className="mt-2 text-sm font-semibold text-slate-900 dark:text-slate-100">User {userId}</p>
                    <div className="mt-1">
                        <StatusBadge status={state?.chat_status || "offline"} size="sm" />
                    </div>
                </div>

                <dl className="space-y-3">
                    {rows.map((row) => {
                        const Icon = row.icon;
                        return (
                            <div key={row.label} className="flex items-start gap-3">
                                <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                                    <Icon className="h-4 w-4" />
                                </span>
                                <div className="min-w-0">
                                    <dt className="text-xs text-slate-400 dark:text-slate-500">{row.label}</dt>
                                    <dd className="truncate text-sm font-medium capitalize text-slate-700 dark:text-slate-200">{row.value}</dd>
                                </div>
                            </div>
                        );
                    })}
                </dl>

                <div className="mt-5 border-t border-slate-100 pt-4 dark:border-slate-800">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">Recent Activity</p>
                    {lastMessage ? (
                        <div className="rounded-xl bg-slate-50 p-3 text-sm dark:bg-slate-800/50">
                            <p className="text-slate-700 dark:text-slate-200">
                                {lastMessage.sender === "cs" ? "Anda: " : ""}
                                {lastMessage.content}
                            </p>
                            <p className="mt-1 text-xs text-slate-400">
                                {new Date(lastMessage.created_at).toLocaleString("id-ID")}
                            </p>
                        </div>
                    ) : (
                        <p className="text-sm text-slate-400">Belum ada aktivitas</p>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function LiveChat() {
    const confirm = useConfirm();
    const [activeTab, setActiveTab] = useState("queue");
    const [conversations, setConversations] = useState({});
    const [userStates, setUserStates] = useState({});
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState("");
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [userInfo, setUserInfo] = useState(null);
    const [infoOpen, setInfoOpen] = useState(false);
    const messagesEndRef = useRef(null);

    useEffect(() => {
        fetchData();

        const msgChannel = supabase
            .channel("public:messages")
            .on(
                "postgres_changes",
                { event: "INSERT", schema: "public", table: "messages" },
                (payload) => handleNewMessage(payload.new)
            )
            .subscribe();

        const stateChannel = supabase
            .channel("public:user_states")
            .on(
                "postgres_changes",
                { event: "*", schema: "public", table: "user_states" },
                (payload) => handleStateUpdate(payload)
            )
            .subscribe();

        return () => {
            supabase.removeChannel(msgChannel);
            supabase.removeChannel(stateChannel);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selectedUserId) {
            const userMsgs = conversations[selectedUserId]?.messages || [];
            const sorted = [...userMsgs].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
            setMessages(sorted);
            scrollToBottom();
        }
    }, [selectedUserId, conversations]);

    // Additive read-only lookup for the info panel
    useEffect(() => {
        let active = true;
        if (!selectedUserId) {
            setUserInfo(null);
            return;
        }
        (async () => {
            try {
                const { data } = await supabase
                    .from("toko")
                    .select("kode_toko, email, jenis")
                    .eq("user_id", selectedUserId)
                    .maybeSingle();
                if (active) setUserInfo(data || null);
            } catch {
                if (active) setUserInfo(null);
            }
        })();
        return () => {
            active = false;
        };
    }, [selectedUserId]);

    const scrollToBottom = () => {
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
    };

    const fetchData = async () => {
        setLoading(true);
        await Promise.all([fetchConversations(), fetchUserStates()]);
        setLoading(false);
    };

    const fetchConversations = async () => {
        const { data, error } = await supabase
            .from("messages")
            .select("*")
            .order("created_at", { ascending: true });

        if (error) {
            console.error("Error fetching messages:", error);
            return;
        }

        const grouped = {};
        data.forEach((msg) => {
            if (!grouped[msg.user_id]) {
                grouped[msg.user_id] = { user_id: msg.user_id, messages: [], lastMessage: msg };
            }
            grouped[msg.user_id].messages.push(msg);
            if (new Date(msg.created_at) > new Date(grouped[msg.user_id].lastMessage.created_at)) {
                grouped[msg.user_id].lastMessage = msg;
            }
        });
        setConversations(grouped);
    };

    const fetchUserStates = async () => {
        const { data, error } = await supabase.from("user_states").select("*");
        if (error) {
            console.error("Error fetching user states:", error);
            return;
        }
        const statesMap = {};
        data.forEach((row) => {
            statesMap[row.user_id] = row.state;
        });
        setUserStates(statesMap);
    };

    const handleNewMessage = (msg) => {
        setConversations((prev) => {
            const newState = { ...prev };

            if (!newState[msg.user_id]) {
                newState[msg.user_id] = { user_id: msg.user_id, messages: [], lastMessage: msg };
            } else {
                newState[msg.user_id] = {
                    ...newState[msg.user_id],
                    messages: [...newState[msg.user_id].messages]
                };
            }

            const exists = newState[msg.user_id].messages.some((m) => m.id === msg.id);
            if (!exists) {
                newState[msg.user_id].messages.push(msg);
            }

            const currentLast = newState[msg.user_id].lastMessage;
            if (!currentLast || new Date(msg.created_at) > new Date(currentLast.created_at)) {
                newState[msg.user_id].lastMessage = msg;
            }

            return newState;
        });

        if (selectedUserId === msg.user_id) scrollToBottom();
    };

    const handleStateUpdate = (payload) => {
        setUserStates((prev) => {
            const newState = { ...prev };
            if (payload.eventType === "DELETE") {
                delete newState[payload.old.user_id];
            } else {
                newState[payload.new.user_id] = payload.new.state;
            }
            return newState;
        });
    };

    const handleAcceptChat = async (userId) => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/admin/accept-chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ user_id: userId }),
            });
            const result = await res.json();
            if (res.ok) {
                toast.success("Chat diterima!");
            } else {
                toast.error("Gagal terima chat: " + result.error);
            }
        } catch (err) {
            toast.error("Error network: " + err.message);
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !selectedUserId) return;

        try {
            const res = await fetch(`${API_BASE_URL}/api/admin/reply-chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ user_id: selectedUserId, message: newMessage.trim() }),
            });
            const result = await res.json();
            if (res.ok) {
                setNewMessage("");
            } else {
                toast.error("Gagal kirim: " + result.error);
            }
        } catch (err) {
            toast.error("Error network: " + err.message);
        }
    };

    const handleEndChat = async () => {
        if (!selectedUserId) return;
        const ok = await confirm({
            title: "Akhiri Sesi Chat",
            message: "Yakin ingin mengakhiri sesi chat ini? User akan dihapus dari antrian/aktif.",
            confirmLabel: "Akhiri Sesi",
            tone: "danger",
        });
        if (!ok) return;

        try {
            const res = await fetch(`${API_BASE_URL}/api/admin/end-chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ user_id: selectedUserId }),
            });
            const result = await res.json();
            if (res.ok) {
                toast.success("Sesi chat diakhiri.");
                setSelectedUserId(null);
            } else {
                toast.error("Gagal akhiri chat: " + result.error);
            }
        } catch (err) {
            toast.error("Error network: " + err.message);
        }
    };

    const queueList = Object.keys(userStates)
        .filter((uid) => userStates[uid].step === "chat_mode" && userStates[uid].chat_status === "waiting")
        .map((uid) => {
            const state = userStates[uid];
            const lastMsg = conversations[uid]?.lastMessage;
            return { user_id: uid, state, lastMessage: lastMsg };
        })
        .sort((a, b) => (a.state.joined_at || 0) - (b.state.joined_at || 0));

    const activeList = Object.keys(conversations)
        .map((uid) => {
            const state = userStates[uid];
            if (state && state.step === "chat_mode" && state.chat_status === "active") {
                return { user_id: uid, state, lastMessage: conversations[uid].lastMessage, active: true };
            }
            if (!state) {
                return { user_id: uid, state: null, lastMessage: conversations[uid].lastMessage, active: false };
            }
            return null;
        })
        .filter(Boolean)
        .sort((a, b) => new Date(b.lastMessage.created_at) - new Date(a.lastMessage.created_at));

    const filteredQueue = queueList.filter((i) => String(i.user_id).toLowerCase().includes(search.toLowerCase()));
    const filteredActive = activeList.filter((i) => String(i.user_id).toLowerCase().includes(search.toLowerCase()));
    const currentList = activeTab === "queue" ? filteredQueue : filteredActive;

    const selectedState = selectedUserId ? userStates[selectedUserId] : null;
    const selectedLastMessage = selectedUserId ? conversations[selectedUserId]?.lastMessage : null;

    const renderListItem = (item, isQueue) => {
        const selected = !isQueue && selectedUserId === item.user_id;
        return (
            <div
                key={item.user_id}
                onClick={() => !isQueue && setSelectedUserId(item.user_id)}
                className={`w-full cursor-pointer border-b border-slate-100 p-3.5 transition-colors last:border-0 dark:border-slate-800 ${
                    selected ? "border-l-4 border-l-primary-500 bg-primary-50/60 dark:bg-primary-500/10" : "hover:bg-slate-50 dark:hover:bg-slate-800/40"
                }`}
            >
                <div className="flex items-start justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2.5">
                        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                            <FiUser className="h-4 w-4" />
                        </span>
                        <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-200">User {item.user_id}</p>
                            {!isQueue && (
                                <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                                    {item.lastMessage ? (item.lastMessage.sender === "cs" ? "Anda: " : "") + item.lastMessage.content : "Belum ada pesan"}
                                </p>
                            )}
                        </div>
                    </div>
                    {isQueue ? (
                        <Button
                            variant="success"
                            size="sm"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleAcceptChat(item.user_id);
                            }}
                        >
                            Terima
                        </Button>
                    ) : (
                        <span className="flex-shrink-0 text-[11px] text-slate-400">
                            {item.lastMessage ? new Date(item.lastMessage.created_at).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }) : ""}
                        </span>
                    )}
                </div>
                {isQueue && (
                    <p className="mt-1.5 flex items-center gap-1 text-xs text-warning-500">
                        <FiClock className="h-3 w-3" /> Menunggu...
                    </p>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-6 animate-fade-in">
            <PageHeader title="Live Chat" subtitle="Layani percakapan pengguna secara real-time" icon={FiMessageSquare} />

            <Card className="overflow-hidden p-0">
                <div className="flex h-[calc(100vh-11rem)] min-h-[520px]">
                    {/* LEFT: list */}
                    <div className={`w-full flex-col border-r border-slate-100 dark:border-slate-800 lg:flex lg:w-80 xl:w-96 ${selectedUserId ? "hidden lg:flex" : "flex"}`}>
                        <div className="flex border-b border-slate-100 dark:border-slate-800">
                            <button
                                onClick={() => setActiveTab("queue")}
                                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                                    activeTab === "queue"
                                        ? "border-b-2 border-warning-500 bg-warning-50/60 text-warning-600 dark:bg-warning-500/10 dark:text-warning-400"
                                        : "text-slate-500 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800/40"
                                }`}
                            >
                                Antrian ({queueList.length})
                            </button>
                            <button
                                onClick={() => setActiveTab("active")}
                                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                                    activeTab === "active"
                                        ? "border-b-2 border-primary-500 bg-primary-50/60 text-primary-600 dark:bg-primary-500/10 dark:text-primary-400"
                                        : "text-slate-500 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800/40"
                                }`}
                            >
                                Aktif ({activeList.filter((i) => i.active).length})
                            </button>
                        </div>

                        <div className="border-b border-slate-100 p-3 dark:border-slate-800">
                            <div className="relative">
                                <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                <input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Cari user ID..."
                                    className="field-base py-2 pl-9"
                                />
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto scrollbar-slim">
                            {loading ? (
                                <p className="p-6 text-center text-sm text-slate-400">Memuat...</p>
                            ) : currentList.length === 0 ? (
                                <EmptyState
                                    icon={FiMessageSquare}
                                    title={activeTab === "queue" ? "Tidak ada antrian" : "Tidak ada chat aktif"}
                                    className="py-10"
                                />
                            ) : (
                                currentList.map((item) => renderListItem(item, activeTab === "queue"))
                            )}
                        </div>
                    </div>

                    {/* CENTER: chat */}
                    <div className={`flex-1 flex-col bg-slate-50/50 dark:bg-slate-950/30 ${selectedUserId ? "flex" : "hidden lg:flex"}`}>
                        {selectedUserId ? (
                            <>
                                <div className="flex items-center gap-3 border-b border-slate-100 bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-900">
                                    <button
                                        onClick={() => setSelectedUserId(null)}
                                        className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 lg:hidden"
                                        aria-label="Kembali"
                                    >
                                        <FiChevronLeft className="h-5 w-5" />
                                    </button>
                                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-50 text-primary-600 dark:bg-primary-500/10 dark:text-primary-400">
                                        <FiUser className="h-4 w-4" />
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">User {selectedUserId}</p>
                                        {selectedState?.step === "chat_mode" ? (
                                            <span className="flex items-center gap-1 text-xs text-success-500">
                                                <span className="h-1.5 w-1.5 rounded-full bg-success-500" /> Live · {selectedState.chat_status}
                                            </span>
                                        ) : (
                                            <span className="text-xs text-slate-400">Offline / Left Chat</span>
                                        )}
                                    </div>
                                    <button
                                        onClick={() => setInfoOpen(true)}
                                        className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 xl:hidden"
                                        aria-label="Info user"
                                    >
                                        <FiInfo className="h-5 w-5" />
                                    </button>
                                    <Button variant="secondary" size="sm" icon={FiXCircle} onClick={handleEndChat} className="text-danger-600 dark:text-danger-400">
                                        Akhiri
                                    </Button>
                                </div>

                                <div className="flex-1 space-y-3 overflow-y-auto p-4 scrollbar-slim">
                                    {messages.map((msg) => (
                                        <div key={msg.id} className={`flex ${msg.sender === "cs" ? "justify-end" : "justify-start"}`}>
                                            <div
                                                className={`max-w-[75%] rounded-2xl px-3.5 py-2.5 text-sm shadow-soft ${
                                                    msg.sender === "cs"
                                                        ? "rounded-br-none bg-primary-600 text-white"
                                                        : "rounded-bl-none border border-slate-200 bg-white text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                                                }`}
                                            >
                                                <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                                                <span className={`mt-1 block text-right text-[10px] ${msg.sender === "cs" ? "text-primary-100" : "text-slate-400"}`}>
                                                    {new Date(msg.created_at).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                    <div ref={messagesEndRef} />
                                </div>

                                <form onSubmit={handleSendMessage} className="border-t border-slate-100 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={newMessage}
                                            onChange={(e) => setNewMessage(e.target.value)}
                                            placeholder="Ketik balasan..."
                                            className="field-base"
                                        />
                                        <Button type="submit" icon={FiSend} disabled={!newMessage.trim()}>
                                            Kirim
                                        </Button>
                                    </div>
                                </form>
                            </>
                        ) : (
                            <div className="flex flex-1 flex-col items-center justify-center text-slate-400">
                                <FiMessageSquare className="mb-3 h-12 w-12 opacity-30" />
                                <p className="text-sm">Pilih percakapan dari daftar</p>
                            </div>
                        )}
                    </div>

                    {/* RIGHT: user info (desktop) */}
                    <div className="hidden w-72 flex-shrink-0 border-l border-slate-100 dark:border-slate-800 xl:block">
                        {selectedUserId ? (
                            <UserInfoPanel userId={selectedUserId} state={selectedState} lastMessage={selectedLastMessage} info={userInfo} />
                        ) : (
                            <div className="flex h-full flex-col items-center justify-center px-6 text-center text-slate-400">
                                <FiInfo className="mb-3 h-10 w-10 opacity-30" />
                                <p className="text-sm">Informasi user akan tampil di sini</p>
                            </div>
                        )}
                    </div>
                </div>
            </Card>

            {/* Mobile info modal */}
            <Modal open={infoOpen} onClose={() => setInfoOpen(false)} title="Informasi User" size="sm">
                {selectedUserId && (
                    <UserInfoPanel userId={selectedUserId} state={selectedState} lastMessage={selectedLastMessage} info={userInfo} />
                )}
            </Modal>
        </div>
    );
}
